/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * Site-wide "reveal on scroll" as progressive enhancement.
 * Content is fully visible if JS is off or the user prefers reduced motion;
 * otherwise elements fade in gently as they enter the viewport.
 *
 * See: https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/
 */

// Elements that should gently reveal as they scroll into view.
// Add `data-reveal` to any element to opt it in manually.
const REVEAL_SELECTORS = [
  '.page-header',
  '.portfolio-intro',
  '.pf-card',
  '.portfolio-more',
  '.cs-header',
  '.cs-meta',
  '.cs-pills',
  '.cs-metrics',
  '.cs-figure',
  '.cs-section',
  '.cs-insight',
  '.section-divider',
  '.pub-impact',
  '.pub-filters',
  '.pub-category',
  '.scholar-link',
  '[data-reveal]',
];

const SELECTOR = REVEAL_SELECTORS.join(',');

let observer = null;
let latePass = null;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function show(node) {
  node.classList.add('is-visible');
}

// Anything already on screen shows immediately: no `.reveal`, nothing to wait
// for, no way for it to get stuck hidden.
function isOnScreen(node) {
  const r = node.getBoundingClientRect();
  return r.top < window.innerHeight && r.bottom > 0;
}

function setupReveal() {
  if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;

  if (observer) {
    observer.disconnect();
    observer = null;
  }

  const nodes = document.querySelectorAll(SELECTOR);

  // React reuses DOM nodes across client-side route changes, and the classes we
  // add here imperatively survive that reuse. Without this reset, a node left
  // at `.reveal` (opacity 0) on the previous page stays invisible on the new
  // one until a hard refresh. Clear the state, then decide again from scratch.
  nodes.forEach((node) => node.classList.remove('reveal', 'is-visible'));

  // Reduced motion: everything stays visible, nothing is observed.
  if (prefersReducedMotion()) return;

  observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          show(entry.target);
          obs.unobserve(entry.target);
        }
      });
    },
    // threshold 0 so a tall section counts the moment its first pixel lands.
    { threshold: 0, rootMargin: '0px 0px -4% 0px' }
  );

  nodes.forEach((node) => {
    if (isOnScreen(node)) return; // visible already, leave it alone
    node.classList.add('reveal');
    observer.observe(node);
  });
}

// Jumping to an anchor drops the reader mid-page without a scroll event.
// Reveal whatever the jump brought into view rather than waiting.
function revealInView() {
  document.querySelectorAll('.reveal:not(.is-visible)').forEach((node) => {
    if (isOnScreen(node)) show(node);
  });
}

function schedule() {
  window.requestAnimationFrame(() => window.requestAnimationFrame(setupReveal));
  // Second pass for content that renders after the first paint (images
  // settling, late hydration). Cheap, and it closes the last timing gap.
  if (latePass) clearTimeout(latePass);
  latePass = setTimeout(() => {
    setupReveal();
    revealInView();
  }, 250);
}

export const onInitialClientRender = () => {
  schedule();
  window.addEventListener('hashchange', revealInView);
};

export const onRouteUpdate = () => {
  schedule();
};
